/**
 * CompaniesPage - List of all covered companies with ratings overview.
 * Features: Grid View (table) + Changes View (grouped by rating changes)
 * Click a row to navigate to /company/:ticker detail page.
 */

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewList as GridIcon,
  SwapVert as ChangesIcon,
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { useLanguage } from '../contexts/LanguageContext';
import { getCompanies, CompanyListItem, prefetchCompanyProfile } from '../services/v3RagService';

const PAGE_SIZE = 50;

type SortField = 'ticker' | 'name' | 'sector' | 'rating' | 'price_target' | 'current_price' | 'upside' | 'rating_change' | 'last_report_date';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'grid' | 'changes';

const RATING_COLORS: Record<string, string> = {
  outperform: '#4caf50',
  overweight: '#4caf50',
  buy: '#4caf50',
  inline: '#9e9e9e',
  'equal-weight': '#9e9e9e',
  neutral: '#9e9e9e',
  hold: '#9e9e9e',
  underperform: '#f44336',
  underweight: '#f44336',
  sell: '#f44336',
};

const getRatingColor = (rating?: string | null): string => {
  if (!rating) return '#9e9e9e';
  return RATING_COLORS[rating.toLowerCase()] || '#9e9e9e';
};

const getConfidenceColor = (confidence?: number | null): string => {
  if (confidence == null) return '#9e9e9e';
  if (confidence >= 0.8) return '#4caf50';
  if (confidence >= 0.5) return '#ff9800';
  return '#f44336';
};

const getConfidenceLabel = (confidence?: number | null): string => {
  if (confidence == null) return 'No confidence data';
  if (confidence >= 0.8) return `High confidence (${(confidence * 100).toFixed(0)}%)`;
  if (confidence >= 0.5) return `Medium confidence (${(confidence * 100).toFixed(0)}%)`;
  return `Low confidence (${(confidence * 100).toFixed(0)}%)`;
};

const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState<string>('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortField, setSortField] = useState<SortField>('ticker');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(value), 250);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  useEffect(() => {
    getCompanies()
      .then(data => setCompanies(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, sectorFilter]);

  const sectors = useMemo(() => {
    const s = new Set<string>();
    companies.forEach(c => { if (c.sector) s.add(c.sector); });
    return Array.from(s).sort();
  }, [companies]);

  const filtered = useMemo(() => {
    let list = companies;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.ticker && c.ticker.toLowerCase().includes(q)) ||
        (c.name && c.name.toLowerCase().includes(q))
      );
    }
    if (sectorFilter) {
      list = list.filter(c => c.sector === sectorFilter);
    }
    return list;
  }, [companies, search, sectorFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let aVal: any = a[sortField] ?? '';
      let bVal: any = b[sortField] ?? '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortField, sortDirection]);

  const visible = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);
  const hasMore = visibleCount < sorted.length;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Changes view grouped data
  const changesGroups = useMemo(() => {
    const groups: { label: string; color: string; items: CompanyListItem[] }[] = [
      { label: 'Upgrades', color: '#4caf50', items: [] },
      { label: 'Downgrades', color: '#f44336', items: [] },
      { label: 'Initiations', color: '#1976d2', items: [] },
      { label: 'Maintained', color: '#9e9e9e', items: [] },
    ];

    for (const c of filtered) {
      const change = c.rating_change?.toLowerCase();
      if (change === 'upgrade' || change === 'soft_upgrade') groups[0].items.push(c);
      else if (change === 'downgrade' || change === 'soft_downgrade') groups[1].items.push(c);
      else if (change === 'initiated' || change === 'initiation' || change === 're-initiation') groups[2].items.push(c);
      else if (change === 'maintain') groups[3].items.push(c);
    }

    return groups.filter(g => g.items.length > 0);
  }, [filtered]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Columns hidden on mobile: subsector, gics, change, date
  const hiddenOnMobile = { display: { xs: 'none', md: 'table-cell' } };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        {t('nav.companies')}
      </Typography>

      {/* Filters */}
      <Box sx={{
        display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center',
        position: { xs: 'static', md: 'sticky' }, top: 0, zIndex: 10,
        bgcolor: 'background.default', py: 1,
      }}>
        <TextField
          size="small"
          placeholder={t('common.search')}
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: '100%', sm: 250 } }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <InputLabel>{t('table.sector')}</InputLabel>
          <Select
            value={sectorFilter}
            label={t('table.sector')}
            onChange={e => setSectorFilter(e.target.value)}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {sectors.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* View Mode Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="grid">
            <GridIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Grid
          </ToggleButton>
          <ToggleButton value="changes">
            <ChangesIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Changes
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {filtered.length} {t('nav.companies').toLowerCase()}
        </Typography>
      </Box>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <Paper elevation={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'ticker'} direction={sortField === 'ticker' ? sortDirection : 'asc'} onClick={() => handleSort('ticker')}>
                      {t('table.ticker')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>
                      {t('table.company')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'sector'} direction={sortField === 'sector' ? sortDirection : 'asc'} onClick={() => handleSort('sector')}>
                      {t('table.sector')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, ...hiddenOnMobile }}>Subsector</TableCell>
                  <TableCell sx={{ fontWeight: 700, ...hiddenOnMobile }}>GICS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'rating'} direction={sortField === 'rating' ? sortDirection : 'asc'} onClick={() => handleSort('rating')}>
                      {t('table.rating')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'price_target'} direction={sortField === 'price_target' ? sortDirection : 'asc'} onClick={() => handleSort('price_target')}>
                      {t('table.target')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'current_price'} direction={sortField === 'current_price' ? sortDirection : 'asc'} onClick={() => handleSort('current_price')}>
                      Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortField === 'upside'} direction={sortField === 'upside' ? sortDirection : 'asc'} onClick={() => handleSort('upside')}>
                      Upside
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, ...hiddenOnMobile }}>{t('table.change')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, ...hiddenOnMobile }}>
                    <TableSortLabel active={sortField === 'last_report_date'} direction={sortField === 'last_report_date' ? sortDirection : 'asc'} onClick={() => handleSort('last_report_date')}>
                      {t('table.date')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map(c => (
                  <TableRow
                    key={c.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/company/${c.ticker}`)}
                    onMouseEnter={() => prefetchCompanyProfile(c.ticker)}
                  >
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {c.ticker}
                    </TableCell>
                    <TableCell>{c.name || c.ticker}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {c.sector || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={hiddenOnMobile}>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {c.sub_sector || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={hiddenOnMobile}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {c.gics_sector || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={c.rating || 'No Rating'}
                          size="small"
                          variant={c.has_rating === false ? 'outlined' : 'filled'}
                          sx={{
                            bgcolor: c.has_rating === false ? 'transparent' : getRatingColor(c.rating) + '20',
                            color: c.has_rating === false ? '#bdbdbd' : getRatingColor(c.rating),
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderStyle: c.has_rating === false ? 'dashed' : 'solid',
                            borderColor: c.has_rating === false ? '#bdbdbd' : undefined,
                          }}
                        />
                        {c.extraction_confidence != null && (
                          <Tooltip title={getConfidenceLabel(c.extraction_confidence)} arrow>
                            <Box sx={{
                              width: 8, height: 8, borderRadius: '50%',
                              bgcolor: getConfidenceColor(c.extraction_confidence),
                              flexShrink: 0,
                            }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{c.price_target ? `$${c.price_target}` : '-'}</TableCell>
                    <TableCell>
                      {c.current_price ? `$${c.current_price.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>
                      {c.upside != null ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: c.upside >= 0 ? '#4caf50' : '#f44336',
                            fontWeight: 600,
                          }}
                        >
                          {c.upside >= 0 ? '+' : ''}{c.upside.toFixed(1)}%
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={hiddenOnMobile}>
                      <Typography variant="body2" color={
                        c.rating_change?.toLowerCase() === 'upgrade' || c.rating_change?.toLowerCase() === 'initiated' ? 'success.main' :
                        c.rating_change?.toLowerCase() === 'downgrade' ? 'error.main' : 'text.secondary'
                      }>
                        {c.rating_change || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={hiddenOnMobile}>
                      <Typography variant="body2" color="text.secondary">
                        {c.last_report_date || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button onClick={() => setVisibleCount(v => v + PAGE_SIZE)}>
                Show more ({sorted.length - visibleCount} remaining)
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Changes View */}
      {viewMode === 'changes' && (
        <Box>
          {changesGroups.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No rating changes found</Typography>
            </Paper>
          ) : (
            changesGroups.map(group => (
              <Paper key={group.label} elevation={2} sx={{ mb: 3 }}>
                <Box sx={{ p: 2, bgcolor: group.color + '15', borderBottom: `2px solid ${group.color}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: group.color }}>
                    {group.label} ({group.items.length})
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {group.items.map(c => (
                        <TableRow
                          key={c.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/company/${c.ticker}`)}
                          onMouseEnter={() => prefetchCompanyProfile(c.ticker)}
                        >
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main', width: 100 }}>
                            {c.ticker}
                          </TableCell>
                          <TableCell>{c.name || c.ticker}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {c.sector || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={c.rating || 'No Rating'}
                                size="small"
                                variant={c.has_rating === false ? 'outlined' : 'filled'}
                                sx={{
                                  bgcolor: c.has_rating === false ? 'transparent' : getRatingColor(c.rating) + '20',
                                  color: c.has_rating === false ? '#bdbdbd' : getRatingColor(c.rating),
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  borderStyle: c.has_rating === false ? 'dashed' : 'solid',
                                  borderColor: c.has_rating === false ? '#bdbdbd' : undefined,
                                }}
                              />
                              {c.extraction_confidence != null && (
                                <Tooltip title={getConfidenceLabel(c.extraction_confidence)} arrow>
                                  <Box sx={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    bgcolor: getConfidenceColor(c.extraction_confidence),
                                    flexShrink: 0,
                                  }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{c.price_target ? `$${c.price_target}` : '-'}</TableCell>
                          <TableCell>
                            {c.upside != null ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: c.upside >= 0 ? '#4caf50' : '#f44336',
                                  fontWeight: 600,
                                }}
                              >
                                {c.upside >= 0 ? '+' : ''}{c.upside.toFixed(1)}%
                              </Typography>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {c.last_report_date || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompaniesPage;
